import { useState, useEffect, useCallback } from 'react';
import { getAddress } from 'viem';
import toast from 'react-hot-toast';
import { config } from '@/config';
import { errorLog } from '@/utils/logger';
import { HEP_TOKEN_ADDRESS } from '@/contracts/HepToken';

interface WalletState {
  address: string | null;
  isConnected: boolean;
  isConnecting: boolean;
  chainId: number | null;
}

interface UseWalletReturn extends WalletState {
  connect: () => Promise<void>;
  disconnect: () => void;
  switchToLocalChain: () => Promise<void>;
  addNetworkToWallet: () => Promise<void>;
  addHepTokenToWallet: () => Promise<boolean>;
  showManualConfig: boolean;
  dismissManualConfig: () => void;
  isMobile: boolean;
  metaMaskDeepLink: string;
}

export function isMobileBrowser(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function getMetaMaskDeepLink(): string {
  const host = window.location.host;
  const path = window.location.pathname + window.location.search;
  return `https://metamask.app.link/dapp/${host}${path}`;
}

export const NETWORK_CONFIG = {
  name: config.blockchain.nativeCurrencyName,
  rpcUrl: config.blockchain.rpcUrl,
  chainId: config.blockchain.chainId,
  symbol: config.blockchain.nativeCurrencySymbol,
  explorerUrl: config.blockchain.explorerUrl,
};

// Local chain configuration matching the backend
const LOCAL_CHAIN = {
  id: config.blockchain.chainId,
  name: config.blockchain.nativeCurrencyName,
  network: 'heperum',
  nativeCurrency: {
    decimals: 18,
    name: config.blockchain.nativeCurrencySymbol,
    symbol: config.blockchain.nativeCurrencySymbol,
  },
  rpcUrls: {
    default: {
      http: [config.blockchain.rpcUrl],
    },
    public: {
      http: [config.blockchain.rpcUrl],
    },
  },
} as const;

declare global {
  interface Window {
    ethereum?: {
      isMetaMask?: boolean;
      request: (args: { method: string; params?: any[] }) => Promise<any>;
      on: (event: string, callback: (data: any) => void) => void;
      removeListener: (event: string, callback: (data: any) => void) => void;
    };
  }
}

export const useWallet = (): UseWalletReturn => {
  const [state, setState] = useState<WalletState>({
    address: null,
    isConnected: false,
    isConnecting: false,
    chainId: null,
  });
  const [showManualConfig, setShowManualConfig] = useState(false);

  // Check if wallet is already connected on mount
  useEffect(() => {
    const checkConnection = async () => {
      if (typeof window !== 'undefined' && window.ethereum) {
        // Respect user's explicit disconnect — don't auto-reconnect
        if (localStorage.getItem('heperum-wallet-disconnected') === 'true') return;
        try {
          const accounts = await window.ethereum.request({
            method: 'eth_accounts',
          });

          if (accounts.length > 0) {
            const chainId = await window.ethereum.request({
              method: 'eth_chainId',
            });

            setState({
              address: getAddress(accounts[0]),
              isConnected: true,
              isConnecting: false,
              chainId: parseInt(chainId, 16),
            });
          }
        } catch (error) {
          errorLog('Failed to check wallet connection:', error);
        }
      }
    };

    checkConnection();
  }, []);

  // Listen for account and chain changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.ethereum) {
      const handleAccountsChanged = (accounts: string[]) => {
        if (localStorage.getItem('heperum-wallet-disconnected') === 'true') return;
        if (accounts.length > 0) {
          setState(prev => ({
            ...prev,
            address: getAddress(accounts[0]),
            isConnected: true,
          }));
        } else {
          setState({
            address: null,
            isConnected: false,
            isConnecting: false,
            chainId: null,
          });
        }
      };

      const handleChainChanged = (chainId: string) => {
        setState(prev => ({
          ...prev,
          chainId: parseInt(chainId, 16),
        }));
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum?.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum?.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, []);

  const connect = useCallback(async () => {
    if (typeof window === 'undefined' || !window.ethereum) {
      toast.error('MetaMask is not installed. Please install MetaMask to continue.');
      return;
    }

    setState(prev => ({ ...prev, isConnecting: true }));
    localStorage.removeItem('heperum-wallet-disconnected');

    try {
      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });

      if (accounts.length === 0) {
        throw new Error('No accounts returned from MetaMask');
      }

      const address = getAddress(accounts[0]);
      
      // Get current chain ID
      const chainId = await window.ethereum.request({
        method: 'eth_chainId',
      });

      setState({
        address,
        isConnected: true,
        isConnecting: false,
        chainId: parseInt(chainId, 16),
      });

      toast.success('Wallet connected successfully!');
    } catch (error: any) {
      setState(prev => ({ ...prev, isConnecting: false }));
      
      if (error.code === 4001) {
        toast.error('Connection rejected by user');
      } else {
        errorLog('Failed to connect wallet:', error);
        toast.error('Failed to connect wallet. Please try again.');
      }
    }
  }, []);

  const disconnect = useCallback(() => {
    localStorage.setItem('heperum-wallet-disconnected', 'true');
    setState({
      address: null,
      isConnected: false,
      isConnecting: false,
      chainId: null,
    });
    toast.success('Wallet disconnected');
  }, []);

  const switchToLocalChain = useCallback(async () => {
    if (typeof window === 'undefined' || !window.ethereum) {
      toast.error('MetaMask is not installed');
      return;
    }

    try {
      // Try to switch to the local chain
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${LOCAL_CHAIN.id.toString(16)}` }],
      });
    } catch (error: any) {
      // If the chain hasn't been added to MetaMask, add it
      if (error.code === 4902) {
        if (window.location.protocol !== 'https:') {
          // HTTP context — MetaMask blocks programmatic network addition
          // Show manual instructions instead
          setShowManualConfig(true);
        } else {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: `0x${LOCAL_CHAIN.id.toString(16)}`,
                  chainName: LOCAL_CHAIN.name,
                  nativeCurrency: LOCAL_CHAIN.nativeCurrency,
                  rpcUrls: LOCAL_CHAIN.rpcUrls.default.http,
                  blockExplorerUrls: [config.blockchain.explorerUrl],
                },
              ],
            });
            toast.success('Local chain added to MetaMask!');
          } catch (addError) {
            errorLog('Failed to add local chain:', addError);
            setShowManualConfig(true);
          }
        }
      } else {
        errorLog('Failed to switch chain:', error);
        toast.error('Failed to switch to local chain');
      }
    }
  }, []);

  const addNetworkToWallet = useCallback(async () => {
    if (typeof window === 'undefined' || !window.ethereum) {
      toast.error('MetaMask is not installed');
      return;
    }
    if (window.location.protocol !== 'https:') {
      toast.error('Automatic network setup only works over HTTPS. Use the manual values below.');
      return;
    }
    try {
      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: `0x${LOCAL_CHAIN.id.toString(16)}`,
          chainName: LOCAL_CHAIN.name,
          nativeCurrency: LOCAL_CHAIN.nativeCurrency,
          rpcUrls: LOCAL_CHAIN.rpcUrls.default.http,
          blockExplorerUrls: [config.blockchain.explorerUrl],
        }],
      });
      toast.success('HEP Mainnet added to MetaMask!');
    } catch (error: any) {
      if (error.code !== 4001) {
        errorLog('Failed to add network:', error);
        toast.error('Could not add network automatically. Use the manual values below.');
      }
    }
  }, []);

  const addHepTokenToWallet = useCallback(async (): Promise<boolean> => {
    if (typeof window === 'undefined' || !window.ethereum) {
      toast.error('MetaMask is not installed');
      return false;
    }

    try {
      // Ensure wallet is on the HEP chain before adding the token
      const chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
      const currentChainId = parseInt(chainIdHex, 16);

      if (currentChainId !== config.blockchain.chainId) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: `0x${config.blockchain.chainId.toString(16)}` }],
          });
          // Chain switched — on mobile, calling wallet_watchAsset immediately after
          // a chain switch is unreliable. Ask the user to tap again.
          toast.success('Switched to Heperum network! Please tap "Add HEP Token" again.');
          return false;
        } catch (switchError: any) {
          if (switchError.code === 4001) {
            toast.error('Please switch to the Heperum network first');
          } else {
            // Chain not added yet (4902) or other error
            toast.error('Please add the Heperum Network first, then try again');
          }
          return false;
        }
      }

      const wasAdded = await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: HEP_TOKEN_ADDRESS,
            symbol: 'HEP',
            decimals: 2,
            image: 'https://timelog.heperum.org/heperum-token.png',
          },
        } as any,
      });

      if (wasAdded) {
        toast.success('HEP token added to MetaMask!');
        return true;
      } else {
        toast.error('Token addition was cancelled');
        return false;
      }
    } catch (error) {
      errorLog('Failed to add token to wallet:', error);
      toast.error('Failed to add HEP token to MetaMask');
      return false;
    }
  }, []);

  const dismissManualConfig = useCallback(() => setShowManualConfig(false), []);

  return {
    ...state,
    connect,
    disconnect,
    switchToLocalChain,
    addNetworkToWallet,
    addHepTokenToWallet,
    showManualConfig,
    dismissManualConfig,
    isMobile: isMobileBrowser(),
    metaMaskDeepLink: getMetaMaskDeepLink(),
  };
};